// Instruction versions of an agent and what each one did (§14 B): runs,
// approval of the drafts and carrier messages, size of the human corrections,
// statuses taken back, errors, duration, cost — with the change against the
// previous version. The agent page reads GET /api/ai-runs/assistants/{id}/
// versions?days= (it feeds the page tiles too) and passes it here. This view
// also lists the recent evaluations of proposed changes (GET /api/
// ai-assistants/{id}/evaluations) and opens a new one (EvaluationPanel).
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, FlaskConical, History } from 'lucide-react';
import { Badge, Banner, Card, EmptyState, Modal, Segmented } from '../design/ui';
import EvaluationPanel from './EvaluationPanel';
import { formatCount, formatPercent, formatShortDay, formatStamp } from './aiAgentsShared';
import './AssistantVersions.css';

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
const ms = (value) => (typeof value === 'number' ? (value < 1000 ? `${Math.round(value)} ms` : `${(value / 1000).toFixed(1).replace('.', ',')} s`) : '—');
const cost = (value) => (typeof value === 'number' ? `$${value.toFixed(4)}` : null);
const points = (x) => `${Math.round(x * 100)} pt`;

export const VERSION_PERIODS = [30, 90, 365];
// French defaults of the existing assistantVersions.origin.* keys.
const ORIGIN_DEFAULTS = {
  created: 'création', manual: 'édition manuelle', improvement: 'amélioration', backfill: 'avant le versionnage', unknown: 'inconnue',
};

// A change against the previous version; `good` says whether a rise is desirable.
const Delta = ({ value, good, format }) => {
  if (typeof value !== 'number' || value === 0) return null;
  const positive = value > 0;
  const desirable = good ? positive : !positive;
  const Icon = positive ? ArrowUp : ArrowDown;
  return (
    <span className={`asv-v2__delta ${desirable ? 'is-better' : 'is-worse'}`} title={format(value)}>
      <Icon aria-hidden="true" />
      {format(Math.abs(value))}
    </span>
  );
};

const AssistantVersions = ({
  assistantId, currentInstructions, onApplied, data, error, days, onDaysChange, onReload,
}) => {
  const { t } = useTranslation();
  const [evaluating, setEvaluating] = useState(false);
  const [evaluations, setEvaluations] = useState(undefined); // undefined = loading, null = failed

  const loadEvaluations = useCallback(async () => {
    if (!assistantId) return;
    try {
      const response = await axios.get(`/api/ai-assistants/${assistantId}/evaluations?limit=5`, authHeaders());
      setEvaluations((response.data && response.data.evaluations) || []);
    } catch (e) {
      setEvaluations(null);
    }
  }, [assistantId]);

  useEffect(() => { loadEvaluations(); }, [loadEvaluations]);

  const versions = (data && data.versions) || [];
  const newestFirst = versions.slice().reverse();
  const withRuns = versions.filter((v) => v.metrics && v.metrics.runs > 0);
  const showCost = withRuns.some((v) => typeof (v.metrics && v.metrics.cost_usd_per_ticket) === 'number');
  const currentHash = data && data.assistant && data.assistant.current_hash;

  const originLabel = (origin) => (origin === 'correction'
    ? t('aiAgentsUi.versions.originCorrection', 'correction de la plateforme')
    : t(`assistantVersions.origin.${origin}`, ORIGIN_DEFAULTS[origin] || origin || '—'));

  const renderTable = () => {
    if (data === undefined) {
      return (
        <div className="asv-v2__skeleton" aria-hidden="true">
          {[0, 1, 2].map((i) => <span key={i} className="u-skeleton" />)}
        </div>
      );
    }
    if (!versions.length) {
      if (error) return null;
      return (
        <EmptyState icon={History} title={t('assistantVersions.empty', 'Aucune version enregistrée pour cet assistant.')} />
      );
    }
    return (
      <div className="u-table-scroll">
        <table className="u-table asv-v2__table">
          <thead>
            <tr>
              <th>{t('assistantVersions.colVersion', 'Version')}</th>
              <th style={{ width: 120 }} className="asv-v2__num">{t('aiAgentsUi.versions.colRuns', 'Traitements')}</th>
              <th style={{ width: 152 }} className="asv-v2__num">{t('aiAgentsUi.versions.colDrafts', 'Brouillons appr.')}</th>
              <th style={{ width: 144 }} className="asv-v2__num">{t('aiAgentsUi.versions.colCarrier', 'Transp. validés')}</th>
              <th style={{ width: 168 }} className="asv-v2__num" title={t('polishPagesUi.versions.correctionsHint', 'Part du texte des brouillons corrigée par votre équipe, et statuts qu’elle a repris')}>
                {t('polishPagesUi.versions.colCorrections', 'Corrections')}
              </th>
              <th style={{ width: 132 }} className="asv-v2__num">{t('assistantVersions.colErrors', 'Erreurs / run')}</th>
              <th style={{ width: 132 }} className="asv-v2__num">{t('assistantVersions.colLatency', 'Durée moy.')}</th>
              {showCost && <th style={{ width: 140 }} className="asv-v2__num">{t('assistantVersions.colCost', 'Coût / ticket')}</th>}
            </tr>
          </thead>
          <tbody>
            {newestFirst.map((version) => {
              const m = version.metrics || {};
              const current = currentHash && version.prompt_hash === currentHash;
              const drafts = (m.approval && m.approval.drafts) || {};
              const carrier = (m.approval && m.approval.carrier_messages) || {};
              const deltas = version.deltas || {};
              const actor = version.actor && version.actor.type !== 'system'
                ? (version.actor.name || version.actor.email || null)
                : null;
              return (
                <tr key={`${version.version}-${version.prompt_hash}`} className={`${current ? 'is-current' : ''}${m.runs ? '' : ' is-idle'}`}>
                  <td title={[formatStamp(version.changed_at), originLabel(version.origin), actor].filter(Boolean).join(' · ')}>
                    <span className="asv-v2__version">
                      <strong>{version.version ? `v${version.version}` : t('assistantVersions.unknown', 'inconnue')}</strong>
                      <span className="asv-v2__day">{formatShortDay(version.changed_at) || '—'}</span>
                      {current && <Badge tone="accent">{t('assistantVersions.current', 'actuelle')}</Badge>}
                    </span>
                    <span className="u-cell-sub">{[originLabel(version.origin), actor].filter(Boolean).join(' · ')}</span>
                  </td>
                  <td className="asv-v2__num u-num">
                    {formatCount(m.runs)}
                    <span className="u-cell-sub">{t('aiAgentsUi.versions.tickets', '{{value}} tickets', { count: m.tickets || 0, value: formatCount(m.tickets) })}</span>
                  </td>
                  <td className="asv-v2__num u-num" title={t('aiAgentsUi.versions.reviewedTitle', '{{count}} relus', { count: drafts.reviewed || 0 })}>
                    {formatPercent(drafts.approval_rate)}
                    <Delta value={deltas.draft_approval_rate} good format={points} />
                    {drafts.reviewed > 0 && <span className="u-cell-sub">{t('aiAgentsUi.versions.reviewed', 'sur {{value}}', { value: formatCount(drafts.reviewed) })}</span>}
                  </td>
                  <td className="asv-v2__num u-num" title={t('aiAgentsUi.versions.reviewedTitle', '{{count}} relus', { count: carrier.reviewed || 0 })}>
                    {formatPercent(carrier.approval_rate)}
                    <Delta value={deltas.carrier_approval_rate} good format={points} />
                    {carrier.reviewed > 0 && <span className="u-cell-sub">{t('aiAgentsUi.versions.reviewed', 'sur {{value}}', { value: formatCount(carrier.reviewed) })}</span>}
                  </td>
                  <td className="asv-v2__num u-num">
                    {typeof m.edit_ratio_mean === 'number'
                      ? t('polishPagesUi.versions.textEdited', '{{rate}} du texte', { rate: formatPercent(m.edit_ratio_mean) })
                      : '—'}
                    <Delta value={deltas.edit_ratio_mean} good={false} format={points} />
                    <span className="u-cell-sub">
                      {t('polishPagesUi.versions.statusOverrides', (m.status_overrides || 0) <= 1 ? '{{value}} statut repris' : '{{value}} statuts repris', { count: m.status_overrides || 0, value: formatCount(m.status_overrides) })}
                    </span>
                  </td>
                  <td className="asv-v2__num u-num">
                    {typeof m.errors_per_run === 'number' ? m.errors_per_run.toFixed(2).replace('.', ',') : '—'}
                    <Delta value={deltas.errors_per_run} good={false} format={(x) => x.toFixed(2).replace('.', ',')} />
                  </td>
                  <td className="asv-v2__num u-num">
                    {ms(m.avg_duration_ms)}
                    <Delta value={deltas.avg_duration_ms} good={false} format={ms} />
                  </td>
                  {showCost && (
                    <td className="asv-v2__num u-num">
                      {cost(m.cost_usd_per_ticket) || '—'}
                      <Delta value={deltas.cost_usd_per_ticket} good={false} format={(x) => `$${x.toFixed(4)}`} />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const evaluationStatus = (evaluation) => {
    if (evaluation.applied_at) {
      return <Badge tone="success" dot>{t('aiAgentsUi.versions.appliedAs', 'Appliquée · v{{version}}', { version: evaluation.applied_version })}</Badge>;
    }
    const tone = { done: undefined, failed: 'danger', queued: 'info', running: 'info' }[evaluation.status];
    const label = evaluation.status === 'done'
      ? t('aiAgentsUi.versions.notApplied', 'Non appliquée')
      : t(`evaluation.status.${evaluation.status}`, { queued: 'en attente', running: 'en cours', failed: 'échec' }[evaluation.status] || evaluation.status);
    return <Badge tone={tone} dot>{label}</Badge>;
  };

  return (
    <div className="asv-v2">
      <Card
        icon={History}
        title={t('assistantVersions.title', 'Versions des instructions')}
        description={t('aiAgentsUi.versions.desc', 'Chaque changement des instructions est une version : ce que l’IA a fait avec, ce que votre équipe a corrigé. Les flèches comparent à la version précédente.')}
        actions={(
          <>
            <Segmented
              label={t('aiAgentsUi.period', 'Période')}
              value={days}
              onChange={onDaysChange}
              options={VERSION_PERIODS.map((value) => ({ value, label: t('aiAgentsUi.periodDays', '{{count}} j', { count: value }) }))}
            />
            <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={() => setEvaluating(true)}>
              <FlaskConical aria-hidden="true" />
              {t('assistantVersions.evaluate', 'Évaluer un changement')}
            </button>
          </>
        )}
        flush
        footerNote={withRuns.length === 0 && versions.length > 0
          ? t('aiAgentsUi.versions.noRuns', 'Aucun traitement ne porte de version sur cette période (marqués depuis le 17 sept. 2026).')
          : undefined}
      >
        {error && (
          <div className="asv-v2__pad">
            <Banner tone="danger" actions={<button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={onReload}>{t('aiAgentsUi.retry', 'Réessayer')}</button>}>
              {error}
            </Banner>
          </div>
        )}
        {renderTable()}
      </Card>

      <Card
        icon={FlaskConical}
        title={t('aiAgentsUi.versions.evaluationsTitle', 'Évaluations récentes')}
        description={t('aiAgentsUi.versions.evaluationsDesc', 'Un changement proposé est rejoué sur les derniers traitements avant d’être appliqué.')}
        flush
      >
        {evaluations === undefined ? (
          <div className="asv-v2__skeleton" aria-hidden="true"><span className="u-skeleton" /></div>
        ) : evaluations === null ? (
          <div className="asv-v2__pad"><Banner tone="danger">{t('aiAgentsUi.versions.evaluationsError', 'Impossible de charger les évaluations.')}</Banner></div>
        ) : !evaluations.length ? (
          <EmptyState icon={FlaskConical} title={t('aiAgentsUi.versions.evaluationsEmpty', 'Aucune évaluation pour l’instant')}>
            {t('aiAgentsUi.versions.evaluationsEmptyHint', '« Évaluer un changement » rejoue vos nouvelles instructions sans rien écrire dans les tickets.')}
          </EmptyState>
        ) : (
          <ul className="u-list">
            {evaluations.map((evaluation) => {
              const score = evaluation.score || {};
              const parts = [];
              if (score.same_decision_rate !== null && score.same_decision_rate !== undefined) parts.push(t('aiAgentsUi.versions.sameDecision', 'même décision {{rate}}', { rate: formatPercent(score.same_decision_rate) }));
              if (score.same_actions_rate !== null && score.same_actions_rate !== undefined) parts.push(t('aiAgentsUi.versions.sameActions', 'mêmes actions {{rate}}', { rate: formatPercent(score.same_actions_rate) }));
              if (score.golden_pass_rate !== null && score.golden_pass_rate !== undefined) parts.push(t('aiAgentsUi.versions.golden', 'jeu de référence {{rate}}', { rate: formatPercent(score.golden_pass_rate) }));
              const proposal = evaluation.proposal || {};
              return (
                <li key={evaluation.id} className="u-row">
                  <div className="u-row__main">
                    <div className="u-row__title">
                      {proposal.from_prompt_version
                        ? t('aiAgentsUi.versions.evaluationOf', 'Changement de la v{{version}}', { version: proposal.from_prompt_version })
                        : t('aiAgentsUi.versions.evaluation', 'Changement proposé')}
                      {typeof proposal.diff_ratio === 'number' && (
                        <span className="u-muted asv-v2__diff">{t('aiAgentsUi.versions.diff', '{{rate}} du texte modifié', { rate: formatPercent(proposal.diff_ratio) })}</span>
                      )}
                    </div>
                    <div className="u-row__meta">
                      {[formatStamp(evaluation.created_at), evaluation.created_by, parts.join(' · ') || evaluation.error].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <div className="u-row__side">
                    {score.golden_gate_passed === false && <Badge tone="danger">{t('aiAgentsUi.versions.goldenFailed', 'jeu de référence en échec')}</Badge>}
                    {evaluationStatus(evaluation)}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {evaluating && (
        <Modal
          wide
          icon={FlaskConical}
          title={t('assistantVersions.evaluateTitle', 'Évaluer un changement d’instructions')}
          description={t('aiAgentsUi.versions.evaluateDesc', 'Modifiez les instructions ci-dessous : elles sont rejouées sur les derniers traitements, puis vous décidez de les appliquer.')}
          onClose={() => setEvaluating(false)}
          closeLabel={t('assistantVersions.close', 'Fermer')}
        >
          <EvaluationPanel
            assistantId={assistantId}
            initialInstructions={currentInstructions || ''}
            onEvaluated={loadEvaluations}
            onApplied={() => { setEvaluating(false); loadEvaluations(); if (onReload) onReload(); if (onApplied) onApplied(); }}
          />
        </Modal>
      )}
    </div>
  );
};

export default AssistantVersions;
